import { useEffect, useMemo, useState } from "react";
import { PALETTE as P } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { deepDiveApi } from "../api/deepDive";
import { usersApi } from "../api/users";

// Deep Dive Analysis surface (mounted on Home and the Intelligence
// feed). Four states:
//
//   1. Locked        (count < 15)         — white card, lock + 21-gate copy
//   2. Teaser        (15 ≤ count < 21)    — white card, progress + preview
//   3. Unlocked-empty (count ≥ 21, none)  — DARK card, Generate CTA
//   4. Generated      (cache exists)      — DARK card, pattern panels
//
// Lock/teaser stay light because they're "you haven't earned this yet"
// states — same visual register as the rest of Profile. Unlocked /
// generated flip dark to mark the analysis as the premium entitlement
// surface.
//
// ``initialState`` is the test seam — production callers never pass it.
//
// ``collapsible`` — Home passes this (Intelligence doesn't). Home's
// daily-action flow was getting pushed below the fold by this card
// rendering fully expanded, so on Home it starts collapsed to a
// one-line, state-appropriate summary with a tap-to-expand affordance;
// expanding reveals the exact same content every other caller already
// shows in full, inline, with a small "Collapse" link to close it back
// up. Intelligence keeps its original always-expanded "standing
// analysis" framing — collapsible is false there, so this is a no-op
// for that caller.

const REFRESH_AGE_DAYS = 3;
const CHECKIN_GATE = 21;          // mirrors app/services/deep_dive_service.py
const ANTIQUE_GOLD = P.primary;
const WARM_BLACK = "#1C1814";
const VELLUM = "#F2EDE3";
const SERIF = "'Playfair Display', serif";


function _daysAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const now = Date.now();
  return Math.floor((now - then) / (1000 * 60 * 60 * 24));
}

function _relativeFromNow(iso) {
  // Cache-transparency phrasing. Floor-based bucketing matches user
  // expectation that "today" covers the whole calendar-equivalent
  // window (anything < 24h since generation). Sister helper to
  // _refreshesIn — together they read as "Last analyzed today · refreshes
  // in 7 days" / "Last analyzed 2 days ago · refreshes in 5 days".
  const days = _daysAgo(iso);
  if (days == null) return "";
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

function _refreshesIn(iso, ttlDays = 7) {
  const days = _daysAgo(iso);
  if (days == null) return "";
  const remaining = Math.max(0, ttlDays - days);
  if (remaining <= 0) return "ready to refresh";
  if (remaining === 1) return "Refreshes in 1 day";
  return `Refreshes in ${remaining} days`;
}

function _pluralCheckIn(n) {
  return `${n} check-in${n === 1 ? "" : "s"}`;
}


// Pulse keyframes injected once per module evaluation. Living next to
// the component keeps the animation scoped — no global stylesheet
// edit, no race with hot-reload. Idempotent ID guard prevents
// double-injection during Vite HMR.
if (typeof document !== "undefined" && !document.getElementById("haiven-dd-pulse-style")) {
  const styleEl = document.createElement("style");
  styleEl.id = "haiven-dd-pulse-style";
  styleEl.textContent = `
    @keyframes haiven-dd-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
  `;
  document.head.appendChild(styleEl);
}


export default function DeepDiveCard({ initialState = null, collapsible = false } = {}) {
  const { user, token, patchUser } = useAuth();
  const [state, setState] = useState(initialState);
  const [loading, setLoading] = useState(initialState == null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(!collapsible);

  useEffect(() => {
    if (initialState != null) return;
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    deepDiveApi.get(user.id, token)
      .then(payload => {
        if (!cancelled) setState(payload);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load your analysis.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, token, initialState]);

  const handleGenerate = async () => {
    if (!user || generating) return;
    setGenerating(true);
    setError(null);
    try {
      const payload = await deepDiveApi.generate(token);
      setState(payload);
    } catch (e) {
      setError(e?.status === 429
        ? "You've hit the analysis rate limit. Try again in a bit."
        : "Couldn't generate your analysis. Please try again."
      );
    } finally {
      setGenerating(false);
    }
  };

  // Consent-required state: opt in (same PATCH Settings uses), then
  // immediately generate — one tap from explanation to analysis.
  const handleEnableAI = async () => {
    if (!user || generating) return;
    setGenerating(true);
    setError(null);
    try {
      await usersApi.setAIConsent(true, token);
      patchUser({ ai_data_consent: true });
      const payload = await deepDiveApi.generate(token);
      setState(payload);
    } catch (e) {
      setError("Couldn't turn on AI analysis. You can also enable it in Settings.");
    } finally {
      setGenerating(false);
    }
  };

  const ageDays = useMemo(
    () => state ? _daysAgo(state.generated_at) : null,
    [state]
  );
  const showRefresh = ageDays != null && ageDays >= REFRESH_AGE_DAYS;
  const patterns = state?.content?.patterns || [];

  // Resolve which shell + content applies, plus a one-line collapsed
  // summary for the 4 "real" states (loading/error have nothing worth
  // hiding, so they always render in full regardless of ``collapsible``
  // — ``summary`` stays null for those and the collapse branch below
  // never triggers).
  let Shell = WhiteShell;
  let content;
  let summary = null;

  if (loading) {
    content = <LoadingRow />;
  } else if (error && !state) {
    content = <ErrorRow message={error} />;
  } else if (state?.locked && !state.teaser) {
    // State 1: bare lock
    const count = state?.checkin_count ?? 0;
    content = <LockedView state={state} />;
    summary = `Unlocks after ${CHECKIN_GATE} check-ins · ${count} so far`;
  } else if (state?.locked && state.teaser) {
    // State 2: teaser
    const remaining = state?.checkins_remaining ?? (CHECKIN_GATE - (state?.checkin_count ?? 0));
    content = <TeaserView state={state} />;
    summary = `Deep Dive unlocks in ${_pluralCheckIn(remaining)}.`;
  } else if (state?.ai_consent_required && patterns.length === 0) {
    // State 3b: unlocked but AI data sharing is off. The entry point
    // stays visible with an honest explanation and a one-tap path to
    // enable — never hidden, never a dead 503.
    Shell = DarkShell;
    content = (
      <ConsentRequiredView
        enabling={generating}
        onEnable={handleEnableAI}
        error={error}
      />
    );
    summary = "Deep Dive needs AI data sharing — tap to learn more.";
  } else if (patterns.length === 0) {
    // State 3: unlocked, no cache
    Shell = DarkShell;
    content = (
      <UnlockedEmptyView
        state={state}
        generating={generating}
        onGenerate={handleGenerate}
        error={error}
      />
    );
    summary = "Your patterns, made visible — tap to generate.";
  } else {
    // State 4: generated
    Shell = DarkShell;
    content = (
      <>
        <GeneratedHeader generatedAt={state.generated_at} />
        {patterns.map((p, i) => (
          <PatternPanel key={i} pattern={p} />
        ))}
        {showRefresh && (
          <RefreshButton onClick={handleGenerate} refreshing={generating} />
        )}
        {error && (
          <p style={{ fontSize: 11, color: "rgba(242,237,227,0.7)", marginTop: 8 }}>{error}</p>
        )}
      </>
    );
    summary = `Last analyzed ${_relativeFromNow(state.generated_at)} · ${_refreshesIn(state.generated_at)}`;
  }

  const isDark = Shell === DarkShell;
  const isCollapsible = collapsible && summary != null;

  if (isCollapsible && !expanded) {
    return (
      <Shell>
        <CollapsedSummaryRow dark={isDark} summary={summary} onExpand={() => setExpanded(true)} />
      </Shell>
    );
  }

  return (
    <Shell>
      {content}
      {isCollapsible && (
        <CollapseFooterLink dark={isDark} onClick={() => setExpanded(false)} />
      )}
    </Shell>
  );
}


// ── Shell wrappers ─────────────────────────────────────────────────


function WhiteShell({ children }) {
  // Light state — sits alongside the rest of Profile's section cards.
  // Uses the existing ``section-card`` class so spacing + border-radius
  // stays consistent with neighbors.
  return (
    <div className="fu d4 section-card" style={{ marginBottom: 16 }}>
      {children}
    </div>
  );
}


function DarkShell({ children }) {
  return (
    <div
      className="fu d4"
      style={{
        marginBottom: 16,
        background: "rgba(28,24,20,0.64)",
        borderRadius: 16,
        padding: "20px 18px",
        boxShadow: "0 4px 24px rgba(28,24,20,0.14)",
      }}
    >
      {children}
    </div>
  );
}


// ── Shared atoms ───────────────────────────────────────────────────


function GoldEyebrow({ children, dark = false }) {
  return (
    <p style={{
      // On the dark card the label is the card's *name* — enlarge,
      // embolden, and switch to bright vellum so users immediately read
      // "Deep Dive Analysis" instead of a dim gold whisper. On light
      // sub-states it stays the small gold eyebrow that matches Profile.
      fontSize: dark ? 13 : 10,
      color: dark ? VELLUM : ANTIQUE_GOLD,
      fontWeight: dark ? 700 : 500,
      letterSpacing: dark ? "0.14em" : "0.08em",
      margin: 0,
    }}>
      {children}
    </p>
  );
}


function ProgressBar({ count, dark = false }) {
  const pct = Math.max(0, Math.min(100, Math.round((count / CHECKIN_GATE) * 100)));
  const track = dark ? "rgba(242,237,227,0.15)" : VELLUM;
  return (
    <div
      role="progressbar"
      aria-valuenow={count}
      aria-valuemin={0}
      aria-valuemax={CHECKIN_GATE}
      style={{
        width: "100%", height: 4, background: track,
        borderRadius: 2, overflow: "hidden",
      }}
    >
      <div style={{
        height: "100%", width: `${pct}%`,
        background: ANTIQUE_GOLD,
        borderRadius: 2,
        transition: "width 200ms ease-out",
      }} />
    </div>
  );
}


// Collapsed state — one line of state-appropriate copy plus a chevron.
// The whole row is the tap target, matching CollapseFooterLink's
// tap-anywhere-to-toggle counterpart below.
function CollapsedSummaryRow({ dark, summary, onExpand }) {
  return (
    <button
      type="button"
      onClick={onExpand}
      aria-expanded="false"
      aria-label={`Deep Dive Analysis. ${summary} Tap to expand.`}
      style={{
        all: "unset", cursor: "pointer", width: "100%", boxSizing: "border-box",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <GoldEyebrow dark={dark}>DEEP DIVE ANALYSIS</GoldEyebrow>
        <p style={{
          fontSize: 12, margin: "4px 0 0", lineHeight: 1.4,
          color: dark ? "rgba(242,237,227,0.75)" : P.textMute,
        }}>
          {summary}
        </p>
      </div>
      <ChevronIcon dark={dark} />
    </button>
  );
}


// Expanded state's way back — sits below the full content rather than
// duplicating the eyebrow every state view already renders at its top.
function CollapseFooterLink({ dark, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded="true"
      aria-label="Collapse Deep Dive Analysis"
      style={{
        all: "unset", cursor: "pointer", display: "flex",
        alignItems: "center", gap: 5, marginTop: 14,
        fontSize: 11, fontWeight: 500, letterSpacing: "0.04em",
        color: dark ? "rgba(242,237,227,0.6)" : P.textMute,
      }}
    >
      <span style={{ transform: "rotate(180deg)", display: "inline-flex" }}>
        <ChevronIcon dark={dark} small />
      </span>
      Collapse
    </button>
  );
}


function ChevronIcon({ dark = false, small = false }) {
  const size = small ? 12 : 16;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={dark ? VELLUM : ANTIQUE_GOLD} strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}


function LoadingRow() {
  return (
    <>
      <GoldEyebrow>DEEP DIVE ANALYSIS</GoldEyebrow>
      <p style={{ fontSize: 13, color: P.textMute, margin: "10px 0 0" }}>
        Loading your analysis…
      </p>
    </>
  );
}


function ErrorRow({ message }) {
  return (
    <>
      <GoldEyebrow>DEEP DIVE ANALYSIS</GoldEyebrow>
      <p style={{ fontSize: 13, color: P.text, margin: "10px 0 0" }}>{message}</p>
    </>
  );
}


// ── State 1: bare lock ─────────────────────────────────────────────


function LockedView({ state }) {
  const count = state?.checkin_count ?? 0;
  return (
    <>
      <GoldEyebrow>DEEP DIVE ANALYSIS</GoldEyebrow>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        margin: "12px 0 14px",
      }}>
        <LockIcon />
        <span style={{ fontSize: 14, color: WARM_BLACK, fontWeight: 500 }}>
          Unlocks after 21 check-ins
        </span>
      </div>
      <ProgressBar count={count} />
      <p style={{
        fontSize: 12, color: P.textMute, margin: "8px 0 0",
        fontVariantNumeric: "tabular-nums",
      }}>
        {_pluralCheckIn(count)} of {CHECKIN_GATE}
      </p>
    </>
  );
}


// ── State 2: teaser ────────────────────────────────────────────────


function TeaserView({ state }) {
  const count = state?.checkin_count ?? 0;
  const remaining = state?.checkins_remaining ?? (CHECKIN_GATE - count);
  return (
    <>
      <GoldEyebrow>DEEP DIVE ANALYSIS</GoldEyebrow>

      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <ProgressBar count={count} />
      </div>

      <p style={{
        fontSize: 14, fontFamily: SERIF, fontStyle: "italic",
        color: WARM_BLACK, lineHeight: 1.4, margin: "4px 0 14px",
      }}>
        Deep Dive unlocks in {_pluralCheckIn(remaining)}.
      </p>

      <div style={{
        height: 1, background: P.border, margin: "0 0 14px",
      }} />

      <p style={{
        fontSize: 10, color: ANTIQUE_GOLD, fontWeight: 500,
        letterSpacing: "0.08em", margin: "0 0 10px",
      }}>
        HERE'S WHAT IT LOOKS AT
      </p>

      <ul style={{
        listStyle: "none", padding: 0, margin: 0,
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        {[
          "Cross-domain patterns — where your mind, body, and behavior diverge",
          "Delayed signals — what shows up 24–48 hours after life events",
          "What's actually driving your best and worst days",
        ].map(line => (
          <li key={line} style={{
            fontSize: 13, color: P.textMid, lineHeight: 1.5,
          }}>
            {line}
          </li>
        ))}
      </ul>
    </>
  );
}


// ── State 3: unlocked, no cache ────────────────────────────────────


function UnlockedEmptyView({ state, generating, onGenerate, error }) {
  const count = state?.checkin_count ?? CHECKIN_GATE;
  return (
    <>
      <GoldEyebrow dark>DEEP DIVE ANALYSIS</GoldEyebrow>
      <p style={{
        fontSize: 22, fontFamily: SERIF, color: VELLUM,
        lineHeight: 1.2, margin: "12px 0 8px",
        whiteSpace: "pre-line",
      }}>
        {"Your patterns,\nmade visible."}
      </p>
      <p style={{
        fontSize: 13, color: "rgba(242,237,227,0.82)", lineHeight: 1.5,
        margin: "0 0 20px",
      }}>
        {count} {count === 1 ? "day" : "days"} of signal. Cross-source patterns you
        haven't seen yet.
      </p>
      <button
        type="button"
        onClick={onGenerate}
        disabled={generating}
        aria-busy={generating ? "true" : "false"}
        style={{
          background: ANTIQUE_GOLD,
          color: VELLUM,
          border: "none",
          borderRadius: 10,
          padding: 14,
          fontSize: 15,
          fontFamily: SERIF,
          fontStyle: "italic",
          cursor: generating ? "default" : "pointer",
          minHeight: 48,
          width: "100%",
          // Pulse animation only while in-flight. No spinner — the copy
          // change + opacity oscillation is the loading signal.
          animation: generating
            ? "haiven-dd-pulse 1.6s ease-in-out infinite"
            : "none",
        }}
      >
        {generating ? "Haiven is analyzing…" : "Generate my analysis"}
      </button>
      {generating ? (
        <p style={{
          fontSize: 11, color: "rgba(158,132,86,0.75)",
          textAlign: "center", margin: "10px 0 0", fontWeight: 300,
        }}>
          Cross-referencing your signal data. This takes about 10 seconds.
        </p>
      ) : (
        <p style={{
          fontSize: 11, color: "rgba(242,237,227,0.62)",
          textAlign: "center", margin: "10px 0 0", fontWeight: 300,
        }}>
          Powered by advanced analysis · Generated once, yours to keep
        </p>
      )}
      {error && (
        <p style={{
          fontSize: 12, color: "rgba(242,237,227,0.85)", marginTop: 10,
          textAlign: "center",
        }}>
          {error}
        </p>
      )}
    </>
  );
}


// ── State 3b: AI data sharing off ──────────────────────────────────


function ConsentRequiredView({ enabling, onEnable, error }) {
  return (
    <>
      <GoldEyebrow dark>DEEP DIVE ANALYSIS</GoldEyebrow>
      <p style={{
        fontSize: 20, fontFamily: SERIF, color: VELLUM,
        lineHeight: 1.25, margin: "12px 0 8px",
      }}>
        Deep Dive uses AI analysis.
      </p>
      <p style={{
        fontSize: 13, color: "rgba(242,237,227,0.82)", lineHeight: 1.55,
        margin: "0 0 6px",
      }}>
        Generating an analysis sends your scores, journal entries, and
        wearable-derived labels to Anthropic (Claude). AI data sharing
        is currently off for your account, so new analyses can't be
        created.
      </p>
      <p style={{
        fontSize: 12, color: "rgba(242,237,227,0.62)", lineHeight: 1.55,
        margin: "0 0 18px",
      }}>
        Everything else — your Signal score, history, and check-ins —
        keeps working without it.
      </p>
      <button
        type="button"
        onClick={onEnable}
        disabled={enabling}
        aria-busy={enabling ? "true" : "false"}
        style={{
          background: ANTIQUE_GOLD,
          color: VELLUM,
          border: "none",
          borderRadius: 10,
          padding: 14,
          fontSize: 15,
          fontFamily: SERIF,
          fontStyle: "italic",
          cursor: enabling ? "default" : "pointer",
          minHeight: 48,
          width: "100%",
          animation: enabling
            ? "haiven-dd-pulse 1.6s ease-in-out infinite"
            : "none",
        }}
      >
        {enabling ? "Turning on…" : "Turn on AI analysis"}
      </button>
      <p style={{
        fontSize: 11, color: "rgba(242,237,227,0.62)",
        textAlign: "center", margin: "10px 0 0", fontWeight: 300,
      }}>
        You can turn it off anytime in Settings.
      </p>
      {error && (
        <p style={{
          fontSize: 12, color: "rgba(242,237,227,0.85)", marginTop: 10,
          textAlign: "center",
        }}>
          {error}
        </p>
      )}
    </>
  );
}


// ── State 4: generated ─────────────────────────────────────────────


function GeneratedHeader({ generatedAt }) {
  // Cache-transparency line. Replaces the older "Last generated <date>"
  // format with a forward-looking "Last analyzed X · Refreshes in N
  // days" that tells the user both how fresh the result is AND when
  // they can next regenerate. Combined with the conditional Refresh
  // button below (≥ 3 days old only), this makes the saved-and-current
  // state legible without a button to mash.
  return (
    <div style={{ marginBottom: 12 }}>
      <GoldEyebrow dark>DEEP DIVE ANALYSIS</GoldEyebrow>
      <p style={{
        fontSize: 11, color: "rgba(158,132,86,0.75)", fontWeight: 400,
        margin: "4px 0 0",
      }}>
        Last analyzed {_relativeFromNow(generatedAt)} · {_refreshesIn(generatedAt)}
      </p>
    </div>
  );
}


function PatternPanel({ pattern }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.10)",
      borderRadius: 10,
      padding: 14,
      marginBottom: 8,
    }}>
      <p style={{
        fontSize: 14, fontFamily: SERIF, color: VELLUM,
        margin: "0 0 6px", lineHeight: 1.3,
      }}>
        {pattern.title}
      </p>
      <p style={{
        fontSize: 13, color: "rgba(242,237,227,0.86)",
        lineHeight: 1.5, margin: "0 0 6px",
      }}>
        {pattern.observation}
      </p>
      <p style={{
        fontSize: 12, color: "rgba(242,237,227,0.7)",
        lineHeight: 1.5, margin: 0, fontStyle: "italic",
      }}>
        {pattern.evidence}
      </p>
      {pattern.implication && (
        <p style={{
          fontSize: 12, color: "rgba(242,237,227,0.7)",
          lineHeight: 1.5, margin: "6px 0 0",
        }}>
          {pattern.implication}
        </p>
      )}
    </div>
  );
}


function RefreshButton({ onClick, refreshing }) {
  // Visible only when the cache is ≥ 3 days old (caller enforces that
  // gate). Shares the analyzing-pulse treatment with the unlocked-empty
  // CTA so the loading state reads the same way in both surfaces.
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={refreshing}
        aria-busy={refreshing ? "true" : "false"}
        style={{
          marginTop: 8,
          background: ANTIQUE_GOLD,
          color: VELLUM,
          border: "none",
          borderRadius: 10,
          padding: 14,
          fontSize: 15,
          fontFamily: SERIF,
          fontStyle: "italic",
          cursor: refreshing ? "default" : "pointer",
          minHeight: 48,
          width: "100%",
          animation: refreshing
            ? "haiven-dd-pulse 1.6s ease-in-out infinite"
            : "none",
        }}
      >
        {refreshing ? "Haiven is analyzing…" : "Refresh analysis"}
      </button>
      {refreshing && (
        <p style={{
          fontSize: 11, color: "rgba(158,132,86,0.75)",
          textAlign: "center", margin: "10px 0 0", fontWeight: 300,
        }}>
          Cross-referencing your signal data. This takes about 10 seconds.
        </p>
      )}
    </>
  );
}


function LockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke={ANTIQUE_GOLD} strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
